import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Menu, MenuItem, Typography, Divider } from '@mui/material';
import { Campaign, Logout, Settings } from '@mui/icons-material';
import { useUserContextController } from '../../context/UserContext';
import { URL_PATHNAMES } from '../routes/AppRoutes';
import AvatarGenerator from '../avatar/avatar';
import LogoutButton from '../logout/LogoutButton';

const AvatarMenu = () => {
  const [userContext] = useUserContextController();
  const { user } = userContext;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          cursor: 'pointer',
          padding: '3px',
          borderRadius: '50%',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: (theme: any) =>
              `${theme.customColors.themePrimary}30`,
            outline: `2px solid ${(theme: any) => theme.customColors.themePrimary}`,
            outlineOffset: '2px'
          }
        }}
        aria-controls={open ? 'account-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <AvatarGenerator
          fullname={user?.fullName || user?.emails[0] || ''}
          imageUrl={user?.profilePictureUrl}
          size={40}
        />
      </Box>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseMenu}
        MenuListProps={{
          'aria-labelledby': 'basic-button'
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        PaperProps={{
          sx: {
            mt: 1.5,
            minWidth: 200,
            borderRadius: 2,
            boxShadow:
              '0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
            border: '1px solid',
            borderColor: (theme) => theme.palette.divider,
            overflow: 'hidden',
            '& .MuiList-root': {
              padding: '8px 0'
            }
          }
        }}
      >
        <MenuItem
          disabled
          sx={{
            padding: '12px 16px',
            opacity: 1,
            cursor: 'default',
            minHeight: 'auto',
            '&:hover': {
              backgroundColor: 'transparent'
            }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              gap: 0.5
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontSize: '15px',
                color: (theme) =>
                  theme.customColors.themeNeutralPrimaryColor || '#1a1a1a',
                lineHeight: 1.4,
                letterSpacing: '-0.01em'
              }}
            >
              {user?.fullName || user?.emails[0] || 'User'}
            </Typography>
          </Box>
        </MenuItem>
        <Divider
          sx={{
            margin: '4px 0',
            borderColor: (theme) => theme.palette.divider
          }}
        />
        <MenuItem
          key="campaigns"
          component={RouterLink}
          to={URL_PATHNAMES.CAMPAIGN}
          onClick={handleCloseMenu}
          disableRipple
          sx={{
            padding: 0,
            minHeight: 'auto',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: (theme) =>
                `${theme.customColors.themePrimary || '#1976d2'}12`,
              '& .menu-item-content': {
                transform: 'translateX(2px)'
              }
            },
            '&.Mui-focusVisible, &.Mui-selected': {
              backgroundColor: 'transparent'
            }
          }}
        >
          <Box
            className="menu-item-content"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              width: '100%',
              padding: '10px 16px',
              borderRadius: 1,
              transition: 'transform 0.2s ease',
              cursor: 'pointer'
            }}
          >
            <Campaign
              sx={{
                fontSize: 18,
                color: (theme) =>
                  theme.customColors.themeNeutralPrimaryColor || '#666',
                transition: 'transform 0.2s ease'
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: '14px',
                color: (theme) =>
                  theme.customColors.themeNeutralPrimaryColor || '#1a1a1a'
              }}
            >
              Campaigns
            </Typography>
          </Box>
        </MenuItem>
        <MenuItem
          key="settings"
          component={RouterLink}
          to={URL_PATHNAMES.SETTINGS}
          onClick={handleCloseMenu}
          disableRipple
          sx={{
            padding: 0,
            minHeight: 'auto',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: (theme) =>
                `${theme.customColors.themePrimary || '#1976d2'}12`,
              '& .menu-item-content': {
                transform: 'translateX(2px)'
              }
            },
            '&.Mui-focusVisible, &.Mui-selected': {
              backgroundColor: 'transparent'
            }
          }}
        >
          <Box
            className="menu-item-content"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              width: '100%',
              padding: '10px 16px',
              borderRadius: 1,
              transition: 'transform 0.2s ease',
              cursor: 'pointer'
            }}
          >
            <Settings
              sx={{
                fontSize: 18,
                color: (theme) =>
                  theme.customColors.themeNeutralPrimaryColor || '#666',
                transition: 'transform 0.2s ease'
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: '14px',
                color: (theme) =>
                  theme.customColors.themeNeutralPrimaryColor || '#1a1a1a'
              }}
            >
              Settings
            </Typography>
          </Box>
        </MenuItem>
        <MenuItem
          key="logout"
          disableRipple
          sx={{
            padding: 0,
            minHeight: 'auto',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: (theme) =>
                `${theme.palette.error.main || '#d32f2f'}12`,
              '& .menu-item-content': {
                transform: 'translateX(2px)'
              }
            },
            '&.Mui-focusVisible, &.Mui-selected': {
              backgroundColor: 'transparent'
            }
          }}
        >
          <Box
            className="menu-item-content"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              width: '100%',
              padding: '10px 16px',
              borderRadius: 1,
              transition: 'transform 0.2s ease',
              cursor: 'pointer'
            }}
          >
            <Logout
              sx={{
                fontSize: 18,
                color: (theme) => theme.palette.error.main || '#d32f2f',
                transition: 'transform 0.2s ease'
              }}
            />
            <LogoutButton />
          </Box>
        </MenuItem>
      </Menu>
    </>
  );
};

export default AvatarMenu;
